import CryptoJS from "crypto-js";

/**
 * UniComply internal field name for the composite identity hash; must match
 * workflow_mapping `Uni_FieldName` in UniComply Rules (Provider/Vendor).
 */
export const EXCLUSION_RECORD_KEY_UNI_FIELD_NAME = "Exclusion Record Key";

export function normalizeNameForExclusionKey(raw: string): string {
  return raw.trim().replace(/\s+/g, " ").toLowerCase();
}

/** US phone: keep 10 digits after stripping non-digits and optional leading 1. */
export function normalizePhoneForExclusionKey(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  let d = digits;
  if (d.length === 11 && d.startsWith("1")) {
    d = d.slice(1);
  }
  return d.length === 10 ? d : "";
}

export function normalizeNpiForExclusionKey(raw: string): string {
  return raw.replace(/\D/g, "");
}

export function normalizeLicenseForExclusionKey(raw: string): string {
  return raw.trim().toUpperCase();
}

export type ExclusionRecordIdentityParts = {
  name: string;
  npi?: string;
  phone?: string;
  license?: string;
};

/**
 * Deterministic string for hashing: name|npi|phone|license (empty segments allowed).
 */
export function buildExclusionRecordCanonicalString(
  parts: ExclusionRecordIdentityParts,
): string {
  const n = normalizeNameForExclusionKey(parts.name ?? "");
  const npi = normalizeNpiForExclusionKey(parts.npi ?? "");
  const phone = normalizePhoneForExclusionKey(parts.phone ?? "");
  const license = normalizeLicenseForExclusionKey(parts.license ?? "");
  return `${n}|${npi}|${phone}|${license}`;
}

/**
 * SHA-256 hex of the canonical identity string (stable across re-runs when inputs match).
 */
export function exclusionRecordIdentityHashSync(
  parts: ExclusionRecordIdentityParts,
): string {
  const canonical = buildExclusionRecordCanonicalString(parts);
  return CryptoJS.SHA256(canonical).toString(CryptoJS.enc.Hex);
}

export async function exclusionRecordIdentityHash(
  parts: ExclusionRecordIdentityParts,
): Promise<string> {
  return exclusionRecordIdentityHashSync(parts);
}
